import React, { useState, useEffect, useRef, useCallback, memo, Suspense } from 'react';
import { 
  OrbitControls,
  PerspectiveCamera,
  Grid,
  TransformControls,
  Html,
  useAnimations
} from '@react-three/drei';
import { Canvas, useThree, useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { 
  Box, 
  Dialog,
  IconButton,
  Tooltip,
  CircularProgress,
  Typography,
  Drawer,
  Divider,
  SpeedDial,
  SpeedDialAction,
  SpeedDialIcon,
  Alert,
  Snackbar,
  Tabs,
  Tab,
  Button,
  Chip,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  Menu as MenuIcon,
  Close as CloseIcon,
  Person as PersonIcon,
  Chair as PropIcon,
  Landscape as EnvironmentIcon,
  Add as AddIcon,
  LibraryBooks as LibraryIcon,
  Settings as ControlsIcon,
  Save as SaveIcon,
  CameraAlt as CameraIcon,
  CropFree as CropIcon,
  Grid3x3 as GridIcon
} from '@mui/icons-material';
import CharacterControls from './CharacterControlPanel';
import CharacterLibrary from './CharacterLibrary';
import PoseControls from './PoseControls';
import { loadCharacter } from '../services/characterService';
import { fetchModels } from '../services/modelService';

// Lightweight safe-frame overlay that fits a target aspect inside a container
const SafeFrameOverlay = ({ containerRef, aspect = 1.78, showThirds = false }) => {
  const [rect, setRect] = useState({ w: 0, h: 0, x: 0, y: 0 });

  useEffect(() => {
    if (!containerRef?.current) return;
    const el = containerRef.current;
    const ro = new ResizeObserver(() => {
      const { width, height } = el.getBoundingClientRect();
      if (!width || !height) return;
      const target = Number(aspect) > 0 && isFinite(Number(aspect)) ? Number(aspect) : 1.78;
      // Fit rect of aspect inside container
      let w = width;
      let h = w / target;
      if (h > height) {
        h = height;
        w = h * target;
      }
      const x = (width - w) / 2;
      const y = (height - h) / 2;
      setRect({ w, h, x, y });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [containerRef, aspect]);

  return (
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        zIndex: 1500
      }}
    >
      {/* Dimmed outside regions */}
      <Box sx={{ position: 'absolute', left: 0, top: 0, width: '100%', height: rect.y, bgcolor: 'rgba(0,0,0,0.3)' }} />
      <Box sx={{ position: 'absolute', left: 0, top: rect.y, width: rect.x, height: rect.h, bgcolor: 'rgba(0,0,0,0.3)' }} />
      <Box sx={{ position: 'absolute', left: rect.x + rect.w, top: rect.y, right: 0, height: rect.h, bgcolor: 'rgba(0,0,0,0.3)' }} />
      <Box sx={{ position: 'absolute', left: 0, top: rect.y + rect.h, width: '100%', bottom: 0, bgcolor: 'rgba(0,0,0,0.3)' }} />
      {/* Safe frame border */}
      <Box
        sx={{
          position: 'absolute',
          left: rect.x,
          top: rect.y,
          width: rect.w,
          height: rect.h,
          border: '2px solid rgba(255,255,255,0.9)',
          boxShadow: '0 0 0 1px rgba(0,0,0,0.6) inset',
          borderRadius: '2px'
        }}
      />
      {/* Rule-of-Thirds grid lines inside the safe frame */}
      {showThirds && (
        <Box
          sx={{
            position: 'absolute',
            left: rect.x,
            top: rect.y,
            width: rect.w,
            height: rect.h,
            pointerEvents: 'none'
          }}
        >
          {/* Vertical lines */}
          <Box sx={{ position: 'absolute', top: 0, bottom: 0, left: `${(1/3)*100}%`, width: '2px', bgcolor: 'rgba(180,180,180,0.9)', boxShadow: '0 0 0 1px rgba(0,0,0,0.15)' }} />
          <Box sx={{ position: 'absolute', top: 0, bottom: 0, left: `${(2/3)*100}%`, width: '2px', bgcolor: 'rgba(180,180,180,0.9)', boxShadow: '0 0 0 1px rgba(0,0,0,0.15)' }} />
          {/* Horizontal lines */}
          <Box sx={{ position: 'absolute', left: 0, right: 0, top: `${(1/3)*100}%`, height: '2px', bgcolor: 'rgba(180,180,180,0.9)', boxShadow: '0 0 0 1px rgba(0,0,0,0.15)' }} />
          <Box sx={{ position: 'absolute', left: 0, right: 0, top: `${(2/3)*100}%`, height: '2px', bgcolor: 'rgba(180,180,180,0.9)', boxShadow: '0 0 0 1px rgba(0,0,0,0.15)' }} />
        </Box>
      )}
    </Box>
  );
};

// (Removed SafeFrameViewport: we no longer letterbox; live preview fills container.)

// Sync the live camera aspect to the safe-frame rect inside the container
const CameraAspectSync = ({ containerRef, targetAspect = 1.78 }) => {
  const { camera, gl } = useThree();
  const aspectRef = useRef({ w: 0, h: 0 });

  useEffect(() => {
    if (!containerRef?.current || !camera) return;
    const el = containerRef.current;
    const ro = new ResizeObserver(() => {
      const { width, height } = el.getBoundingClientRect();
      if (!width || !height) return;
      const a = Number(targetAspect) > 0 && isFinite(Number(targetAspect)) ? Number(targetAspect) : 1.78;
      // Fit rect of aspect inside container
      let w = width;
      let h = w / a;
      if (h > height) {
        h = height;
        w = h * a;
      }
      aspectRef.current = { w, h };
      const nextAspect = w / h;
      if (typeof camera.aspect === 'number' && Math.abs(camera.aspect - nextAspect) > 1e-3) {
        camera.aspect = nextAspect;
        camera.updateProjectionMatrix?.();
        // Also ensure viewport matches container to avoid subtle scaling differences
        gl.setViewport(0, 0, gl.domElement.width, gl.domElement.height);
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [containerRef, targetAspect, camera, gl]);

  return null;
};

// Smooth camera controls and persistence of camera/target
const CameraDampingAndPersist = () => {
  const { camera, controls } = useThree();

  // Apply smooth controls once controls exist
  useEffect(() => {
    if (!controls) return;
    controls.enableDamping = true;
    controls.dampingFactor = 0.12;
    controls.rotateSpeed = 0.9;
    controls.zoomSpeed = controls.zoomSpeed ?? 1.2; // respect JSX value if set
    controls.panSpeed = 0.8;
    controls.update && controls.update();
  }, [controls]);

  // Restore persisted camera state
  useEffect(() => {
    try {
      const raw = localStorage.getItem('threeD_camera_state');
      if (!raw) return;
      const parsed = JSON.parse(raw);
      const pos = parsed?.position;
      const tgt = parsed?.target;
      const fov = parsed?.fov;
      if (Array.isArray(pos) && pos.length === 3) {
        camera.position.set(pos[0], pos[1], pos[2]);
      }
      if (typeof fov === 'number' && camera.fov !== fov) {
        camera.fov = fov;
        camera.updateProjectionMatrix?.();
      }
      if (controls && Array.isArray(tgt) && tgt.length === 3) {
        controls.target.set(tgt[0], tgt[1], tgt[2]);
        controls.update && controls.update();
      }
    } catch {}
  }, [camera, controls]);

  // Persist camera state on control changes (throttled)
  useEffect(() => {
    if (!controls) return;
    let t = null;
    const onChange = () => {
      if (t) return;
      t = setTimeout(() => {
        t = null;
        try {
          const snapshot = {
            position: [camera.position.x, camera.position.y, camera.position.z],
            target: controls ? [controls.target.x, controls.target.y, controls.target.z] : [0, 100, 0],
            fov: camera.fov,
          };
          localStorage.setItem('threeD_camera_state', JSON.stringify(snapshot));
        } catch {}
      }, 200);
    };
    controls.addEventListener?.('change', onChange);
    return () => {
      controls.removeEventListener?.('change', onChange);
      if (t) clearTimeout(t);
    };
  }, [camera, controls]);

  return null;
};

// Helper to capture a snapshot of the current WebGL canvas
const SnapshotHelper = ({
  trigger,
  onCapture,
  characters,
  settings
}) => {
  const { gl, scene, camera, size, controls } = useThree();

  useEffect(() => {
    if (!trigger) return;
    if (!gl || !scene || !camera) return;

    // Preserve current renderer state
    const prevSize = { width: size.width, height: size.height };
    const prevPixelRatio = gl.getPixelRatio();
    const prevAlpha = gl.getClearAlpha();
    const prevViewport = gl.getContext().getParameter(gl.getContext().VIEWPORT);
    const prevCameraAspect = camera.aspect;

    try {
      const { width = 1280, height = 720, transparent = false } = settings || {};
      gl.setPixelRatio(1);
      gl.setSize(width, height, false);
      // Ensure camera aspect matches export dimensions
      if (typeof camera.aspect === 'number') {
        camera.aspect = width / height;
        if (camera.updateProjectionMatrix) camera.updateProjectionMatrix();
      }
      // Set viewport to match export
      gl.setViewport(0, 0, width, height);
      if (transparent) gl.setClearAlpha(0);

      // Ensure controls are updated to latest camera state
      if (controls && controls.update) controls.update();
      gl.render(scene, camera);

      const dataUrl = gl.domElement.toDataURL('image/png');

      const cam = camera;
      const target = controls && controls.target 
        ? [controls.target.x, controls.target.y, controls.target.z]
        : [0, 100, 0];

      const sceneState = {
        camera: {
          position: [cam.position.x, cam.position.y, cam.position.z],
          target,
          fov: cam.fov
        },
        characters: characters.map(c => ({
          id: c.id,
          url: c.url,
          format: c.format,
          position: c.position,
          rotation: c.rotation,
          scale: c.scale,
          poseData: c.poseData || null,
          isPlaying: c.isPlaying,
          animationSpeed: c.animationSpeed
        }))
      };

      onCapture?.(dataUrl, sceneState);
    } catch (e) {
      console.error('Snapshot failed:', e);
      onCapture?.(null, null, e);
    } finally {
      // Restore renderer state
      gl.setSize(prevSize.width, prevSize.height, false);
      gl.setPixelRatio(prevPixelRatio);
      gl.setClearAlpha(prevAlpha);
      // Restore viewport
      if (prevViewport && prevViewport.length === 4) {
        gl.setViewport(prevViewport[0], prevViewport[1], prevViewport[2], prevViewport[3]);
      }
      // Restore camera aspect
      if (typeof prevCameraAspect === 'number') {
        camera.aspect = prevCameraAspect;
        if (camera.updateProjectionMatrix) camera.updateProjectionMatrix();
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trigger]);

  return null;
};

// Ground component
const Ground = memo(() => {
  return (
    <mesh 
      rotation={[-Math.PI / 2, 0, 0]} 
      position={[0, 0, 0]} 
      receiveShadow
    >
      <planeGeometry args={[1000, 1000]} /> {/* 10m x 10m in centimeters */}
      <meshStandardMaterial 
        color="#f0f0f0"
        roughness={1}
        metalness={0}
      />
    </mesh>
  );
});

// DropZone component for drag-and-drop character placement
const DropZone = memo(({ onDrop }) => {
  const { camera, gl } = useThree();
  const planeRef = useRef();

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();

    try {
      const characterData = JSON.parse(e.dataTransfer.getData('application/json'));
      if (!characterData) return;

      // Convert screen coordinates to world coordinates
      const rect = gl.domElement.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      const y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
      
      // Create a ray from the camera
      const raycaster = new THREE.Raycaster();
      raycaster.setFromCamera(new THREE.Vector2(x, y), camera);
      
      // Find intersection with the ground plane
      const intersects = raycaster.intersectObject(planeRef.current);
      if (intersects.length > 0) {
        const point = intersects[0].point;
        onDrop(characterData, [point.x, 0, point.z]);
      }
    } catch (error) {
      console.error('Error handling character drop:', error);
    }
  };

  return (
    <mesh 
      ref={planeRef}
      rotation={[-Math.PI / 2, 0, 0]} 
      position={[0, 0, 0]}
      visible={false}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      <planeGeometry args={[1000, 1000]} />
      <meshBasicMaterial />
    </mesh>
  );
});

// Character component with animations and pose controls
const Character = memo(({ 
  url, 
  format,
  position, 
  rotation, 
  scale, 
  onSelect, 
  isSelected,
  onTransform,
  animationIndex = 0,
  isPlaying = true,
  poseData = null,
  onBonesLoaded
}) => {
  const groupRef = useRef();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [character, setCharacter] = useState(null);
  const [skeleton, setSkeleton] = useState(null);
  const [bones, setBones] = useState({});
  const [mixer, setMixer] = useState(null);
  const [currentAnimation, setCurrentAnimation] = useState(null);

  // Normalize bone names to improve compatibility across rigs (e.g., Mixamo prefixes)
  const normalizeBoneName = (name) => {
    if (!name) return name;
    let n = name;
    // Strip common prefixes
    n = n.replace(/^mixamorig[:_]?/i, '');
    n = n.replace(/^Armature[|:_-]?/i, '');
    n = n.replace(/^Root[|:_-]?/i, '');
    // Replace separators
    n = n.replace(/[|.\s]+/g, '');
    return n;
  };

  useEffect(() => {
    const loadCharacterModel = async () => {
      if (!url || !format) return;

      try {
        setLoading(true);
        setError(null);

        const encodedUrl = url.split('/').map(part => encodeURIComponent(part)).join('/');
        const loadedCharacter = await loadCharacter(encodedUrl, format);
        
        // Extract skeleton and bones
        let characterSkeleton = null;
        const boneMap = {};
        
        loadedCharacter.traverse((node) => {
          if (node.isBone) {
            const key = normalizeBoneName(node.name);
            if (key && !boneMap[key]) {
              boneMap[key] = node;
            }
          }
          if (node.isSkinnedMesh && !characterSkeleton) {
            characterSkeleton = node.skeleton;
          }
        });

        setSkeleton(characterSkeleton);
        setBones(boneMap);
        
        // Notify parent component about loaded bones
        onBonesLoaded?.(boneMap);

        // Calculate standard scale
        if (format.toLowerCase() === 'fbx') {
          const box = new THREE.Box3().setFromObject(loadedCharacter);
          const size = box.getSize(new THREE.Vector3());
          const targetHeight = 180;
          const standardScale = targetHeight / size.y;
          loadedCharacter.scale.set(standardScale, standardScale, standardScale);
        } else if (scale) {
          loadedCharacter.scale.set(scale, scale, scale);
        }
        
        if (loadedCharacter.animations?.length > 0) {
          const newMixer = new THREE.AnimationMixer(loadedCharacter);
          setMixer(newMixer);
          const anim = loadedCharacter.animations[0];
          if (anim) {
            const action = newMixer.clipAction(anim);
            action.play();
            setCurrentAnimation(action);
          }
        }

        setCharacter(loadedCharacter);
        setLoading(false);
      } catch (err) {
        console.error('Error loading character:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    loadCharacterModel();

    return () => {
      if (mixer) {
        mixer.stopAllAction();
        mixer.uncacheRoot(character);
      }
      if (character) {
        character.traverse((child) => {
          if (child.geometry) {
            child.geometry.dispose();
          }
          if (child.material) {
            if (Array.isArray(child.material)) {
              child.material.forEach(material => material.dispose());
            } else {
              child.material.dispose();
            }
          }
        });
      }
    };
  }, [url, format, scale]);

  // Handle animation playback
  useEffect(() => {
    if (currentAnimation) {
      if (isPlaying) {
        currentAnimation.play();
      } else {
        currentAnimation.stop();
      }
    }
  }, [isPlaying, currentAnimation]);

  // Update mixer and re-apply pose on each frame so animation doesn't override it
  useFrame((state, delta) => {
    if (mixer && isPlaying) {
      mixer.update(delta);
    }
    if (poseData && bones) {
      Object.entries(poseData).forEach(([boneName, rotation]) => {
        const bone = bones[normalizeBoneName(boneName)] || bones[boneName];
        if (bone && rotation) {
          bone.rotation.set(
            THREE.MathUtils.degToRad(rotation.x || 0),
            THREE.MathUtils.degToRad(rotation.y || 0),
            THREE.MathUtils.degToRad(rotation.z || 0)
          );
        }
      });
    }
  });

  // Update transform controls
  useEffect(() => {
    if (groupRef.current && onTransform) {
      onTransform(groupRef.current);
    }
  }, [onTransform]);

  // Apply pose data when it changes
  useEffect(() => {
    if (poseData && bones) {
      Object.entries(poseData).forEach(([boneName, rotation]) => {
        const bone = bones[normalizeBoneName(boneName)] || bones[boneName];
        if (bone) {
          bone.rotation.set(
            THREE.MathUtils.degToRad(rotation.x || 0),
            THREE.MathUtils.degToRad(rotation.y || 0),
            THREE.MathUtils.degToRad(rotation.z || 0)
          );
        }
      });
    }
  }, [poseData, bones]);

  if (loading) {
    return (
      <Html center>
        <CircularProgress size={24} />
      </Html>
    );
  }

  if (error) {
    return (
      <Html center>
        <Typography color="error" variant="caption">
          {error}
        </Typography>
      </Html>
    );
  }

  if (!character) return null;

  return (
    <group
      ref={groupRef}
      position={position}
      rotation={rotation}
      onClick={(e) => {
        e.stopPropagation();
        onSelect?.();
      }}
    >
      <primitive object={character} />
    </group>
  );
});

// Scene component with camera and controls
const Scene = memo(({ children, onCharacterDrop }) => {
  return (
    <>
      <color attach="background" args={['#f0f0f0']} />
      <PerspectiveCamera 
        makeDefault 
        position={[0, 200, 500]} 
        fov={60}
      />
      <OrbitControls 
        makeDefault
        target={[0, 100, 0]}
        maxPolarAngle={Math.PI / 2}
        minDistance={10}
        maxDistance={1000}
        zoomSpeed={1.5}
      />
      <ambientLight intensity={0.5} />
      <directionalLight
        position={[50, 50, 25]}
        castShadow
        intensity={1}
        shadow-mapSize-width={2048}
        shadow-mapSize-height={2048}
      />
      <Ground />
      <DropZone onDrop={onCharacterDrop} />
      <gridHelper args={[1000, 100]} />
      {children}
    </>
  );
});

// Main ThreeDScene component
const ThreeDScene = ({
  open,
  onClose,
  onExportImage, // (pngDataUrl, sceneState) => void
  url,
  format,
  scale,
  onBonesLoaded,
  onTransform,
  poseData,
  isPlaying = true,
  animationSpeed = 1,
  aspectRatio: incomingAspectRatio = 1.78
}) => {
  const [characters, setCharacters] = useState([]);
  const [selectedCharacter, setSelectedCharacter] = useState(null);
  const [characterBones, setCharacterBones] = useState({});
  const [showSidePanel, setShowSidePanel] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [quickAccessOpen, setQuickAccessOpen] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [snapshotTrigger, setSnapshotTrigger] = useState(0);
  const [exportSettings, setExportSettings] = useState({ width: 1280, height: 720, transparent: false });
  const sceneContainerRef = useRef(null);
  const [showSafeFrame, setShowSafeFrame] = useState(true);
  const [showThirdsGrid, setShowThirdsGrid] = useState(false);

  const uiAspect = (() => {
    const a = typeof incomingAspectRatio === 'number' && isFinite(incomingAspectRatio) && incomingAspectRatio > 0
      ? incomingAspectRatio
      : 1.78;
    return a;
  })();

  // Derive export size from aspect ratio, keeping height near 720
  useEffect(() => {
    const a = typeof incomingAspectRatio === 'number' && isFinite(incomingAspectRatio) && incomingAspectRatio > 0
      ? incomingAspectRatio
      : 1.78;
    const targetHeight = 720; // baseline
    const targetWidth = Math.round(targetHeight * a);
    setExportSettings(prev => ({ ...prev, width: targetWidth, height: targetHeight }));
  }, [incomingAspectRatio]);

  // Load persisted UI prefs
  useEffect(() => {
    try {
      const sf = localStorage.getItem('threeD_showSafeFrame');
      const tg = localStorage.getItem('threeD_showThirdsGrid');
      if (sf !== null) setShowSafeFrame(sf === 'true');
      if (tg !== null) setShowThirdsGrid(tg === 'true');
    } catch {}
  }, []);

  // Persist UI prefs
  useEffect(() => {
    try {
      localStorage.setItem('threeD_showSafeFrame', String(showSafeFrame));
      localStorage.setItem('threeD_showThirdsGrid', String(showThirdsGrid));
    } catch {}
  }, [showSafeFrame, showThirdsGrid]);

  // Keyboard shortcuts: S snapshot, F safe frame, G thirds
  useEffect(() => {
    const onKey = (e) => {
      if (e.target && (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.isContentEditable)) return;
      if (e.key.toLowerCase() === 's') {
        setSnapshotTrigger((v) => v + 1);
      } else if (e.key.toLowerCase() === 'f') {
        setShowSafeFrame((v) => !v);
      } else if (e.key.toLowerCase() === 'g') {
        setShowThirdsGrid((v) => !v);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Add quick access actions
  const quickAccessActions = [
    { icon: <PersonIcon />, name: 'Add Character', type: 'character' },
    { icon: <PropIcon />, name: 'Add Prop', type: 'prop' },
    { icon: <EnvironmentIcon />, name: 'Add Environment', type: 'environment' }
  ];

  const handleQuickAdd = async (type) => {
    try {
      setLoading(true);
      setError(null);
      const models = await fetchModels(type);
      if (models.length > 0) {
        const newCharacter = {
          ...models[0],
          id: Date.now().toString(),
          position: [0, 0, 0],
          rotation: [0, 0, 0],
          scale: 1,
          isPlaying: true,
          animationSpeed: 1
        };
        setCharacters(prev => [...prev, newCharacter]);
        setSelectedCharacter(newCharacter.id);
      } else {
        setError(`No ${type} models available. Please add some models first.`);
      }
    } catch (error) {
      console.error('Error loading models:', error);
      setError(`Failed to load ${type} models. Please try again.`);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    onClose?.();
  };

  const handleAddCharacter = (characterData, position = [0, 0, 0]) => {
    const newCharacter = {
      ...characterData,
      id: Date.now().toString(),
      position,
      rotation: [0, 0, 0],
      scale: 1,
      isPlaying: true,
      animationSpeed: 1
    };
    setCharacters(prev => [...prev, newCharacter]);
    setSelectedCharacter(newCharacter.id);
    setActiveTab(1); // Switch to controls tab after adding character
  };

  const handleCharacterDrop = (characterData, position) => {
    handleAddCharacter(characterData, position);
  };

  const handleDeleteCharacter = (characterId) => {
    setCharacters(prev => prev.filter(char => char.id !== characterId));
    if (selectedCharacter === characterId) {
      setSelectedCharacter(null);
    }
  };

  const handleUpdateCharacter = (characterId, updates) => {
    setCharacters(prev => prev.map(char => 
      char.id === characterId ? { ...char, ...updates } : char
    ));
  };

  const handlePoseChange = (poseData) => {
    if (selectedCharacter) {
      handleUpdateCharacter(selectedCharacter, { poseData });
    }
  };

  const handleBonesLoaded = (characterId, bones) => {
    setCharacterBones(prev => ({
      ...prev,
      [characterId]: bones
    }));
  };

  const selectedCharacterData = characters.find(char => char.id === selectedCharacter);

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="xl"
      fullWidth
    >
      <Box sx={{ 
        display: 'flex', 
        height: '80vh',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <IconButton
          onClick={handleClose}
          sx={{
            position: 'absolute',
            top: 12,
            left: 12,
            zIndex: 2500,
            bgcolor: 'background.paper',
            '&:hover': {
              bgcolor: 'action.hover',
            },
            boxShadow: 1
          }}
        >
          <CloseIcon />
        </IconButton>
        <Box ref={sceneContainerRef} sx={{ 
          flexGrow: 1,
          position: 'relative',
          transition: 'margin 0.3s ease',
          ...(showSidePanel && {
            marginRight: '300px'
          })
        }}>
          {/* Top Toolbar */}
          <Box
            sx={{
              position: 'absolute',
              top: 12,
              left: 56,
              right: 12,
              zIndex: 2100,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              flexWrap: 'wrap',
              bgcolor: 'rgba(18,18,18,0.55)',
              backdropFilter: 'saturate(120%) blur(8px)',
              border: '1px solid rgba(255,255,255,0.08)',
              borderRadius: 2,
              px: 1.5,
              py: 0.75,
              boxShadow: '0 8px 24px rgba(0,0,0,0.25)'
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              3D Composer
            </Typography>
            <Chip 
              size="small" 
              label={`AR ${uiAspect.toFixed(2)}`}
              variant="outlined" 
              sx={{ 
                color: 'white', 
                borderColor: 'rgba(255,255,255,0.2)',
                display: { xs: 'none', sm: 'inline-flex' }
              }} 
            />
            <Box sx={{ flexGrow: 1 }} />
            <Tooltip title={showSafeFrame ? 'Hide Safe Frame (F)' : 'Show Safe Frame (F)'} placement="bottom">
              <IconButton size="small" onClick={() => setShowSafeFrame(v => !v)} color={showSafeFrame ? 'primary' : 'default'} sx={{ color: showSafeFrame ? 'primary.main' : 'rgba(255,255,255,0.7)' }}>
                <CropIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={showThirdsGrid ? 'Hide Thirds Grid (G)' : 'Show Thirds Grid (G)'} placement="bottom">
              <span>
                <IconButton size="small" onClick={() => setShowThirdsGrid(v => !v)} disabled={!showSafeFrame} color={showThirdsGrid ? 'primary' : 'default'} sx={{ color: !showSafeFrame ? 'rgba(255,255,255,0.3)' : (showThirdsGrid ? 'primary.main' : 'rgba(255,255,255,0.7)') }}>
                  <GridIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Button variant="contained" color="primary" size="small" startIcon={<CameraIcon />} onClick={() => setSnapshotTrigger(v => v + 1)}>
              Snapshot
            </Button>
          </Box>
          <Canvas shadows gl={{ preserveDrawingBuffer: true, alpha: true }}>
            <Suspense fallback={null}>
              <CameraAspectSync containerRef={sceneContainerRef} targetAspect={incomingAspectRatio} />
              <Scene onCharacterDrop={handleCharacterDrop}>
                {characters.map((char) => (
                  <Character
                    key={char.id}
                    url={char.url}
                    format={char.format}
                    position={char.position}
                    rotation={char.rotation}
                    scale={char.scale}
                    isSelected={selectedCharacter === char.id}
                    onSelect={() => setSelectedCharacter(char.id)}
                    onTransform={selectedCharacter === char.id ? (obj) => {
                      handleUpdateCharacter(char.id, {
                        position: [obj.position.x, obj.position.y, obj.position.z],
                        rotation: [obj.rotation.x, obj.rotation.y, obj.rotation.z],
                        scale: obj.scale.x
                      });
                    } : null}
                    poseData={char.poseData}
                    onBonesLoaded={(bones) => handleBonesLoaded(char.id, bones)}
                  />
                ))}
                {selectedCharacter && (
                  <TransformControls
                    mode="translate"
                    size={0.5}
                  />
                )}
                {/* Invisible helper that captures PNG when snapshotTrigger changes */}
                <SnapshotHelper
                  trigger={snapshotTrigger}
                  characters={characters}
                  settings={exportSettings}
                  onCapture={(png, sceneState) => {
                    if (png && onExportImage) onExportImage(png, sceneState);
                  }}
                />
              </Scene>
            </Suspense>
          </Canvas>
          {/* Safe-frame overlay to compose framing to export aspect */}
          {showSafeFrame && (
            <SafeFrameOverlay containerRef={sceneContainerRef} aspect={incomingAspectRatio} showThirds={showThirdsGrid} />
          )}

          {/* Quick Access Menu */}
          <SpeedDial
            ariaLabel="Quick Access Menu"
            sx={{ position: 'absolute', bottom: 16, right: 16, zIndex: 2200 }}
            icon={<SpeedDialIcon />}
            onClose={() => setQuickAccessOpen(false)}
            onOpen={() => setQuickAccessOpen(true)}
            open={quickAccessOpen}
          >
            {quickAccessActions.map((action) => (
              <SpeedDialAction
                key={action.name}
                icon={action.icon}
                tooltipTitle={action.name}
                onClick={() => handleQuickAdd(action.type)}
              />
            ))}
            <SpeedDialAction
              key="Snapshot"
              icon={<SaveIcon />}
              tooltipTitle="Snapshot"
              onClick={() => setSnapshotTrigger(Date.now())}
            />
          </SpeedDial>
        </Box>

        {/* Combined Side Panel */}
        <Drawer
          anchor="right"
          open={showSidePanel}
          variant="persistent"
          sx={{
            width: 300,
            flexShrink: 0,
            position: 'absolute',
            right: 0,
            height: '100%',
            '& .MuiDrawer-paper': {
              width: 300,
              position: 'relative',
              height: '100%',
              border: 'none',
              borderLeft: 1,
              borderColor: 'divider',
              transform: 'none !important'
            },
          }}
        >
          <Box sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Tabs 
                value={activeTab} 
                onChange={(e, newValue) => setActiveTab(newValue)}
                sx={{ flex: 1 }}
              >
                <Tab 
                  icon={<LibraryIcon />} 
                  label="Library" 
                  sx={{ minWidth: 0, flex: 1 }}
                />
                {selectedCharacterData && (
                  <Tab 
                    icon={<ControlsIcon />} 
                    label="Controls" 
                    sx={{ minWidth: 0, flex: 1 }}
                  />
                )}
              </Tabs>
            </Box>
            <Divider sx={{ mb: 2 }} />

            {/* Library Tab */}
            {activeTab === 0 && (
              <Box sx={{ flex: 1, overflow: 'auto' }}>
                <CharacterLibrary
                  selectedCharacter={selectedCharacterData}
                  onCharacterSelect={(character) => {
                    handleAddCharacter(character);
                  }}
                  onDeleteCharacter={handleDeleteCharacter}
                />
              </Box>
            )}

            {/* Controls Tab */}
            {activeTab === 1 && selectedCharacterData && (
              <Box>
                <CharacterControls
                  character={selectedCharacterData}
                  onUpdate={handleUpdateCharacter}
                />
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle1" sx={{ mb: 2 }}>
                  Pose Controls
                </Typography>
                <PoseControls
                  bones={characterBones[selectedCharacter] || {}}
                  onPoseChange={(poseData) => handlePoseChange(poseData)}
                />
              </Box>
            )}
          </Box>
        </Drawer>

        {/* Show panel button when hidden */}
        {!showSidePanel && (
          <Tooltip title="Show Panel" placement="left">
            <IconButton
              onClick={() => setShowSidePanel(true)}
              sx={{
                position: 'absolute',
                right: 0,
                top: 10,
                backgroundColor: 'background.paper',
                borderRadius: '4px 0 0 4px',
                '&:hover': {
                  backgroundColor: 'action.hover',
                }
              }}
            >
              <MenuIcon />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Dialog>
  );
};

export default ThreeDScene;
